class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.shipping = document.getElementById('cart-notification-shipping');
    this.heading = document.getElementById('cart-notification-heading');
    this.productContainer = document.getElementById('cart-notification-product');
    this.total = document.getElementById('cart-notification-total');
    this.cartIcon = document.getElementById('cart-icon-bubble');
    this.closeTimeout = null;
    this.freeShippingThreshold = this.getFreeShippingThreshold();
    this.freeShippingItemLimit = this.getFreeShippingItemLimit();
    this.cartRequestController = null;
    this.lineUpdateController = null;
    this.lineUpdateRequestId = 0;
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onNotificationClick = this.handleNotificationClick.bind(this);
    this.onNotificationChange = this.handleNotificationChange.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.notification.addEventListener('click', this.onNotificationClick);
    this.notification.addEventListener('change', this.onNotificationChange);
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    if (this.cartIcon) {
      this.cartIcon.setAttribute('role', 'button');
      this.cartIcon.setAttribute('aria-haspopup', 'dialog');
      this.cartIcon.addEventListener('click', (event) => {
        event.preventDefault();
        this.setActiveElement(this.cartIcon);
        this.fetchCart()
          .then((cart) => this.renderCart(cart, { mode: 'view' }))
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      });
    }
  }

  open(timeout = 4200) {
    window.clearTimeout(this.closeTimeout);
    this.notification.classList.add('animate', 'active');
    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );
    document.body.addEventListener('click', this.onBodyClick);
    this.closeTimeout = window.setTimeout(() => this.close(), timeout);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    window.clearTimeout(this.closeTimeout);
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    if (parsedState.sections?.['cart-icon-bubble']) {
      const cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble) {
        cartBubble.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
      }
    }

    if (typeof parsedState.total_price === 'number') {
      this.renderCart(parsedState, { mode: 'added' });
      return;
    }

    this.fetchCart()
      .then((cart) => this.renderCart(cart, { mode: 'added' }))
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  fetchCart() {
    this.cartRequestController?.abort();
    const controller = new AbortController();
    this.cartRequestController = controller;

    return fetch(`${routes.cart_url}.js`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart fetch failed with status ${response.status}`);
        return response.json();
      })
      .finally(() => {
        if (this.cartRequestController === controller) this.cartRequestController = null;
      });
  }

  renderCart(cart, { mode = 'view' } = {}) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const totalPrice = this.normalizeMoneyAmount(cart.total_price);
    const itemCount = this.getItemCount(cart, items);
    const qualifiesForFreeShipping = totalPrice >= this.freeShippingThreshold && itemCount <= this.freeShippingItemLimit;
    this.heading.textContent = mode === 'added' ? 'カートに追加しました' : 'カートの内容';
    this.shipping.textContent = this.getShippingMessage(totalPrice, itemCount);
    this.shipping.classList.toggle('is-free', qualifiesForFreeShipping);
    this.total.replaceChildren(this.createMoneyNode(totalPrice));
    this.productContainer.replaceChildren(items.length ? this.renderItems(items) : this.createEmptyStateNode());
    this.open(mode === 'view' ? 6000 : 4200);
  }

  renderItems(items) {
    const fragment = document.createDocumentFragment();

    items.forEach((item, index) => {
      fragment.appendChild(this.createItemNode(item, index));
    });

    return fragment;
  }

  renderItemState(item) {
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    const fragment = document.createDocumentFragment();

    options.forEach((option) => {
      const state = document.createElement('p');
      state.className = 'cart-notification-product__state';
      state.textContent = `${option.name}: ${option.value}`;
      fragment.appendChild(state);
    });

    return fragment;
  }

  renderQuantityOptions(selectedQuantity) {
    const maxQuantity = Math.max(10, Number(selectedQuantity || 1));
    return Array.from({ length: maxQuantity }, (_, index) => {
      const value = index + 1;
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = String(value);
      option.selected = value === selectedQuantity;
      return option;
    });
  }

  handleNotificationClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || target.disabled) return;

    const { action, line } = target.dataset;
    const parsedLine = Number(line);
    if (!parsedLine) return;

    if (action === 'remove') {
      this.updateLineQuantity(parsedLine, 0);
      return;
    }

    if (action !== 'decrease' && action !== 'increase') return;

    const quantityControl = target.closest('.cart-notification-product__quantity');
    const quantitySelect = quantityControl?.querySelector('.cart-notification-product__quantity-select');
    if (!quantitySelect) return;

    const currentQuantity = Number(quantitySelect.value || 1);
    const nextQuantity = action === 'decrease' ? Math.max(1, currentQuantity - 1) : currentQuantity + 1;
    quantitySelect.value = String(nextQuantity);
    this.updateLineQuantity(parsedLine, nextQuantity);
  }

  handleNotificationChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || target.disabled) return;
    if (target.dataset.action !== 'quantity') return;

    const line = Number(target.dataset.line);
    const quantity = Number(target.value || 1);
    if (!line || !quantity) return;

    this.updateLineQuantity(line, quantity);
  }

  updateLineQuantity(line, quantity) {
    const requestId = ++this.lineUpdateRequestId;

    this.lineUpdateController?.abort();
    const controller = new AbortController();
    this.lineUpdateController = controller;
    this.setControlsDisabled(true);

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        line,
        quantity,
        sections: ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart line update failed with status ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        if (requestId !== this.lineUpdateRequestId) return;
        this.renderContents(cart);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== this.lineUpdateRequestId) return;
        console.error(error);
      })
      .finally(() => {
        if (requestId !== this.lineUpdateRequestId) return;
        if (this.lineUpdateController === controller) this.lineUpdateController = null;
        this.setControlsDisabled(false);
      });
  }

  getShippingMessage(totalPrice, itemCount) {
    const remaining = Math.max(this.freeShippingThreshold - totalPrice, 0);
    if (remaining === 0 && itemCount > this.freeShippingItemLimit) {
      return `クリックポスト送料無料は${this.freeShippingItemLimit}点以下のご注文が対象です`;
    }
    if (remaining === 0) return 'クリックポストでの送料無料対象です';
    return `残り${Number(remaining).toLocaleString('ja-JP')}円でクリックポストでの送料無料`;
  }

  getFreeShippingThreshold() {
    const threshold = Number(this.notification?.dataset.freeShippingThreshold);
    return Number.isFinite(threshold) && threshold > 0 ? threshold : 3500;
  }

  getFreeShippingItemLimit() {
    const itemLimit = Number(this.notification?.dataset.freeShippingItemLimit);
    return Number.isFinite(itemLimit) && itemLimit > 0 ? itemLimit : 300;
  }

  getItemCount(cart, items) {
    if (Number.isFinite(Number(cart.item_count))) return Number(cart.item_count);
    return items.reduce((count, item) => count + Number(item.quantity || 0), 0);
  }

  createEmptyStateNode() {
    const empty = document.createElement('p');
    empty.className = 'cart-notification__empty';
    empty.textContent = 'カートは空です。';
    return empty;
  }

  createItemNode(item, index) {
    const content = document.createElement('div');
    content.className = 'cart-notification-product__content';

    if (item.featured_image?.url) {
      const imageWrap = document.createElement('div');
      imageWrap.className = 'cart-notification-product__image';
      const image = document.createElement('img');
      image.src = this.getSafeUrl(item.featured_image.url);
      image.alt = item.featured_image.alt || '';
      image.width = 52;
      image.height = 52;
      image.loading = 'lazy';
      imageWrap.appendChild(image);
      content.appendChild(imageWrap);
    }

    const details = document.createElement('div');
    details.className = 'cart-notification-product__details';

    const name = document.createElement('p');
    name.className = 'cart-notification-product__name';
    name.textContent = item.product_title || '';
    details.appendChild(name);

    const states = this.renderItemState(item);
    if (states.childNodes.length) details.appendChild(states);

    const metaRow = document.createElement('div');
    metaRow.className = 'cart-notification-product__meta-row';

    const quantityWrap = document.createElement('div');
    quantityWrap.className = 'cart-notification-product__quantity';
    quantityWrap.dataset.line = String(index + 1);

    const decrease = document.createElement('button');
    decrease.type = 'button';
    decrease.className = 'cart-notification-product__quantity-button';
    decrease.dataset.action = 'decrease';
    decrease.dataset.line = String(index + 1);
    decrease.setAttribute('aria-label', `${item.product_title || ''}の数量を減らす`);
    decrease.textContent = '-';

    const select = document.createElement('select');
    select.className = 'cart-notification-product__quantity-select';
    select.dataset.action = 'quantity';
    select.dataset.line = String(index + 1);
    select.setAttribute('aria-label', `${item.product_title || ''}の数量`);
    select.append(...this.renderQuantityOptions(item.quantity));

    const increase = document.createElement('button');
    increase.type = 'button';
    increase.className = 'cart-notification-product__quantity-button';
    increase.dataset.action = 'increase';
    increase.dataset.line = String(index + 1);
    increase.setAttribute('aria-label', `${item.product_title || ''}の数量を増やす`);
    increase.textContent = '+';

    quantityWrap.append(decrease, select, increase);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cart-notification-product__remove';
    remove.dataset.action = 'remove';
    remove.dataset.line = String(index + 1);
    remove.textContent = '削除';

    metaRow.append(quantityWrap, remove);
    details.appendChild(metaRow);
    content.appendChild(details);

    const price = document.createElement('p');
    price.className = 'cart-notification-product__price';
    price.appendChild(this.createMoneyNode(this.normalizeMoneyAmount(item.final_line_price ?? item.final_price)));
    content.appendChild(price);

    return content;
  }

  normalizeMoneyAmount(amount) {
    return Math.round(Number(amount || 0) / 100);
  }

  createMoneyNode(amount) {
    const money = document.createElement('span');
    money.className = 'money';

    const value = document.createElement('span');
    value.className = 'money__amount';
    value.textContent = Number(amount).toLocaleString('ja-JP');

    const currency = document.createElement('span');
    currency.className = 'money__currency';
    currency.textContent = '円';

    money.append(value, currency);
    return money;
  }

  setControlsDisabled(disabled) {
    this.notification
      .querySelectorAll('.cart-notification-product__quantity-button, .cart-notification-product__quantity-select, .cart-notification-product__remove')
      .forEach((element) => {
        element.disabled = disabled;
      });
  }

  getSafeUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
    } catch (error) {
      return '';
    }
  }
}

customElements.define('cart-notification', CartNotification);
